import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, ChevronRight, Menu, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import NotificationBell from './NotificationBell';
import LangToggle from './LangToggle';
import axios from 'axios';
import { useSite } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = ({ activeTab, setActiveTab, searchQuery, setSearchQuery, onSearch, onVideoClick }) => {
  const { theme } = useSite();
  const logoSrc = theme?.logo_url || '/logo.png';
  const [catOpen, setCatOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const [categories, setCategories] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeout = useRef(null);
  const catBtnRef = useRef(null);
  const catRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/categories`).then(r => setCategories(r.data || [])).catch(() => {});
    axios.get(`${BACKEND_URL}/api/nav`).then(r => {
      if (r.data && r.data.length > 0) {
        const items = r.data.filter(n => n.visible !== false).sort((a, b) => (a.order || 0) - (b.order || 0)).map(n => {
          let tabId = 'best', isDropdown = false, children = [];
          const lt = n.link_type || '';
          if (lt === 'all') tabId = 'best';
          else if (lt === 'terbaru') tabId = 'terbaru';
          else if (lt === 'trending') tabId = 'trending';
          else if (lt === 'cerita') { tabId = 'cerita'; if (n.children && n.children.length > 0) isDropdown = true; }
          else if (lt === 'categories-dropdown') { tabId = 'categories'; isDropdown = true; }
          else if (lt === "category") tabId = (n.link_category || "").toLowerCase();
          // Any item with children becomes dropdown
          if (n.children && n.children.length > 0) {
            isDropdown = true;
            children = n.children.map(c => ({ id: (c.link_category || c.label || "").toLowerCase(), label: c.label || c.link_category || "" }));
          }
          return { id: tabId, label: n.label, dropdown: isDropdown, link_type: lt, link_category: n.link_category, children };
        });
        setNavItems(items);
      } else {
        setNavItems([
          { id: 'best', label: 'Home' },
          { id: 'categories', label: 'Categories', dropdown: true },
          { id: 'terbaru', label: 'Bokep Terbaru' },
          { id: 'trending', label: 'Trending' },
          { id: 'cerita', label: 'Cerita Dewasa' },
        ]);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (catRef.current && !catRef.current.contains(e.target) && catBtnRef.current && !catBtnRef.current.contains(e.target)) setCatOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleExpand = (id) => {
    setExpandedMenus(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCatToggle = (tab) => {
    const btn = document.querySelector(`[data-nav-id="${tab?.id || 'cat'}"]`);
    if (!catOpen && btn) {
      const rect = btn.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 2, left: rect.left });
    }
    if (catOpen && activeDropdown === tab?.id) {
      setCatOpen(false); setActiveDropdown(null);
    } else {
      setCatOpen(true); setActiveDropdown(tab?.id || 'cat');
    }
  };

  const handleNavClick = (tab) => {
    if (tab.dropdown) {
      if (window.innerWidth <= 768) return;
      handleCatToggle(tab);
    } else if (tab.link_type === 'category' && tab.link_category) {
      setActiveTab(tab.link_category.toLowerCase()); setCatOpen(false); setMobileMenu(false);
    } else if (tab.id === 'cerita' && tab.dropdown) {
      // Cerita with dropdown - toggle dropdown on desktop, direct on mobile
      if (window.innerWidth <= 768) return;
      handleCatToggle(tab);
    } else { setActiveTab(tab.id); setCatOpen(false); setMobileMenu(false); }
  };

  const handleMobileSelect = (tabId) => {
    setActiveTab(tabId);
    setMobileMenu(false);
    if (onSearch) onSearch('');
  };

  const handleSearchInput = (val) => {
    setSearchQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.length >= 2) {
      searchTimeout.current = setTimeout(() => {
        axios.get(BACKEND_URL + "/api/search/suggest?q=" + encodeURIComponent(val))
          .then(r => { setSuggestions(r.data || []); setShowSuggestions(true); })
          .catch(() => {});
      }, 300);
    } else {
      setSuggestions([]); setShowSuggestions(false);
    }
  };
  const handleSearch = (e) => { e.preventDefault(); if (onSearch) onSearch(searchQuery); setMobileMenu(false); setShowSuggestions(false); };
  const handleSuggestionClick = (s) => {
    if (s.type === 'video' && onVideoClick) {
      axios.get(BACKEND_URL + "/api/videos/" + s.id).then(r => onVideoClick(r.data)).catch(() => {});
    } else if (s.type === 'category') {
      setActiveTab(s.id);
    }
    setShowSuggestions(false); setSearchQuery('');
  };

  // Get children for dropdown items
  const getDropdownChildren = (tab) => {
    if (tab.children && tab.children.length > 0) return tab.children;
    if (tab.link_type === "categories-dropdown" || (tab.dropdown && !tab.children)) {
      return categories.map(c => ({ id: c.name.toLowerCase(), label: c.name }));
    }
    return [];
  };

  return (
    <header className="site-header">
      {/* Desktop Header */}
      <div className="header-top header-desktop">
        <a href="/" className="logo-link"><img loading="lazy" src={logoSrc} alt="Logo" /></a>
        <div className="search-wrapper">
          <form onSubmit={handleSearch} className="search-box">
            <input type="text" placeholder="Search videos..." value={searchQuery} onChange={(e) => handleSearchInput(e.target.value)} onFocus={() => suggestions.length > 0 && setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} className="search-input" />
            <button type="submit" className="search-icon-btn"><Search size={17} /></button>
          </form>
          {showSuggestions && suggestions.length > 0 && (
            <div className="search-suggestions">
              {suggestions.map((s, i) => (
                <div key={i} className="search-suggestion-item" onMouseDown={() => handleSuggestionClick(s)}>
                  {s.thumbnail && <img src={s.thumbnail} alt="" style={{width:40,height:28,objectFit:"cover",borderRadius:4}} />}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:600,color:"#ddd",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.title}</div>
                    <span style={{fontSize:9,color:"#666",textTransform:"uppercase",fontWeight:700}}>{s.type === "category" ? "Category" : s.category || "Video"}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="header-controls">
          <NotificationBell onVideoClick={onVideoClick} />
          <ThemeToggle />
          <LangToggle />
        </div>
      </div>

      {/* Mobile Header */}
      <div className="header-mobile">
        <div className="header-mobile-top">
          <a href="/" className="logo-link"><img loading="lazy" src={logoSrc} alt="Logo" /></a>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <NotificationBell onVideoClick={onVideoClick} />
            <button className="mobile-hamburger" onClick={() => setMobileMenu(!mobileMenu)}>
              {mobileMenu ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        <form onSubmit={handleSearch} className="header-mobile-search">
          <input type="text" placeholder="Search videos..." value={searchQuery} onChange={(e) => handleSearchInput(e.target.value)} className="search-input" />
          <button type="submit" className="search-icon-btn"><Search size={16} /></button>
        </form>
      </div>

      {/* Desktop Nav */}
      <nav className="nav-bar nav-desktop">
        <div className="nav-inner">
          {navItems.map((tab) => (
            <div key={tab.id + tab.label} className="nav-item-wrap">
              <button data-nav-id={tab.id} className={`nav-item ${activeTab === tab.id ? 'active' : ''}`} onClick={() => handleNavClick(tab)}>
                {tab.label}
                {tab.dropdown && <ChevronDown size={12} style={{ marginLeft: 4, transform: catOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />}
              </button>
            </div>
          ))}
        </div>
      </nav>

      {/* Mobile Slide Menu */}
      {mobileMenu && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenu(false)}>
          <div className="mobile-menu-panel" onClick={e => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <img loading="lazy" src={logoSrc} alt="Logo" style={{ height: 30 }} />
              <button className="mobile-menu-close" onClick={() => setMobileMenu(false)}><X size={20} /></button>
            </div>

            <div className="mobile-menu-items">
              {navItems.map(tab => {
                const isExpanded = expandedMenus[tab.id];
                const children = getDropdownChildren(tab);

                if (tab.dropdown && children.length > 0) {
                  return (
                    <div key={tab.id + tab.label}>
                      <button className={`mobile-menu-item has-dropdown ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleExpand(tab.id)}>
                        <span>{tab.label}</span>
                        <span className={`mobile-menu-arrow ${isExpanded ? 'open' : ''}`}>
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="mobile-submenu">
                          {children.map(child => (
                            <button key={child.id} className={`mobile-submenu-item ${activeTab === child.id ? 'active' : ''}`}
                              onClick={() => {
                                if (tab.id === 'cerita' || tab.link_type === 'cerita') {
                                  setActiveTab('cerita');
                                } else {
                                  setActiveTab(child.id);
                                }
                                setMobileMenu(false);
                              }}>
                              {child.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <button key={tab.id + tab.label} className={`mobile-menu-item ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => handleMobileSelect(tab.id)}>
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Category Dropdown */}
      {catOpen && (
        <div ref={catRef} className="cat-dropdown" style={{ top: dropPos.top, left: dropPos.left }}>
          {(() => {
            const activeNav = navItems.find(n => n.id === activeDropdown);
            if (activeNav && activeNav.children && activeNav.children.length > 0) {
              return activeNav.children.map((child, i) => (
                <button key={i} className="cat-dropdown-item" onClick={() => { 
                  if (activeNav.id === 'cerita') { setActiveTab('cerita'); }
                  else { setActiveTab(child.id || child.link_category?.toLowerCase()); }
                  setCatOpen(false); setActiveDropdown(null); if (onSearch) onSearch(''); 
                }}>
                  {child.label}
                </button>
              ));
            }
            return categories.map((cat) => (
              <button key={cat.id} className="cat-dropdown-item" onClick={() => { setActiveTab(cat.name.toLowerCase()); setCatOpen(false); setActiveDropdown(null); if (onSearch) onSearch(''); }}>
                {cat.name} {cat.count > 0 ? `(${cat.count})` : ''}
              </button>
            ));
          })()}
        </div>
      )}
    </header>
  );
};

export default Header;
